import React from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useQueries } from "@tanstack/react-query";
import { ArrowRight } from "@dreamhub/designsystem/icons";
import {
  DropdownMenuItem,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import IconContainer from "@/components/ui/icon-container";
import { useAuth } from "@/services/auth/AuthContext";
import { useSwitchAccount } from "@/services/auth/useSwitchAccount";
import { useAPIHelper as fetchAPIData } from "@/services/api/hook";
import { AccountData } from "@/entities/accounts/types";

const NAME_STALE_TIME_MS = 5 * 60 * 1000;

// The API client turns a 401 into an "UNAUTHORIZED" error and leaves a 403 as
// the raw axios error; either means the user can't act in that account.
const isAccessDenied = (error: unknown): boolean =>
  (error instanceof Error && error.message === "UNAUTHORIZED") ||
  (axios.isAxiosError(error) && error.response?.status === 403);

/**
 * Reads each account's name with X-Tenant-Id set to that account (GET
 * /accounts returns the account the request acts in). These requests never
 * go through the auth retry handler: an account the user can't read is
 * simply left out of the menu, without a re-login or a switch.
 */
const useAccountNames = (accountIds: string[]) => {
  const { isAuthenticated, token, tokenExpiry } = useAuth();

  return useQueries({
    queries: accountIds.map((accountId) => ({
      queryKey: ["linked-account-name", accountId],
      enabled: isAuthenticated,
      staleTime: NAME_STALE_TIME_MS,
      retry: false,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchAPIData(
          { token, tenantId: accountId, tokenExpiry },
          ["accounts", {}],
          signal
        ) as Promise<AccountData>
    }))
  });
};

// User-menu entries that move between the home account and its child
// accounts. The account already active is never offered, and a child is left
// out while its name loads or when the user can't read it; the way home is
// always offered from a child.
export const AccountSwitchMenuItem: React.FC = () => {
  const { t } = useTranslation();
  const { activeAccountId, homeTenantId, childAccountIds, switchTo } =
    useSwitchAccount();

  const accountIds =
    homeTenantId && childAccountIds.length > 0
      ? [homeTenantId, ...childAccountIds.filter((id) => id !== homeTenantId)]
      : [];
  const names = useAccountNames(accountIds);

  const targets = accountIds.flatMap((accountId, index) => {
    const result = names[index];
    const isChild = accountId !== homeTenantId;
    const isUnreadableChild = isChild && isAccessDenied(result?.error);
    const isUnresolvedChild = isChild && (!result || result.isPending);
    if (
      accountId === activeAccountId ||
      isUnreadableChild ||
      isUnresolvedChild
    ) {
      return [];
    }
    return [{ accountId, name: result?.data?.name || accountId }];
  });

  if (targets.length === 0) {
    return null;
  }

  return (
    <>
      {targets.map(({ accountId, name }) => (
        <DropdownMenuItem
          key={accountId}
          data-test={`switch-account-${accountId}`}
          onSelect={() =>
            switchTo(accountId === homeTenantId ? null : accountId)
          }
        >
          <div className="flex flex-row items-center justify-start px-1">
            <IconContainer size="5" color="secondary">
              <ArrowRight />
            </IconContainer>
            <DropdownMenuLabel>
              {t("accountSwitch.switchTo", { name })}
            </DropdownMenuLabel>
          </div>
        </DropdownMenuItem>
      ))}
    </>
  );
};
